import { onUnmounted } from "vue";
import { useMutation, useQueryClient } from "@tanstack/vue-query";
import Unipile from "@/services/unipile/unipile";
import type { SendEmailParams } from "./types";

/**
 * Composable for sending emails with TanStack mutation.
 *
 * Bug fix: After a successful send, invalidates email-conversations immediately
 * AND schedules a delayed re-invalidation after 3 seconds to catch the API
 * indexing delay (Unipile.sendEmail returns before the email provider has
 * indexed the sent email).
 */
export function useSendEmail() {
  const queryClient = useQueryClient();
  const pendingTimeouts: ReturnType<typeof setTimeout>[] = [];

  // Cleanup pending timeouts on unmount
  onUnmounted(() => {
    pendingTimeouts.forEach((id) => clearTimeout(id));
    pendingTimeouts.length = 0;
  });

  const sendEmailMutation = useMutation({
    mutationFn: async (params: SendEmailParams) => {
      const response = await Unipile.sendEmail(params.payload);
      return response.data;
    },
    onSuccess: () => {
      // Immediate invalidation (first refetch attempt — may still be stale)
      queryClient.invalidateQueries({
        queryKey: ["email-conversations"],
      });

      // Delayed re-invalidation after 3s to catch newly indexed email
      const timeoutId = setTimeout(() => {
        queryClient.invalidateQueries({
          queryKey: ["email-conversations"],
        });
        // Remove from pending list
        const idx = pendingTimeouts.indexOf(timeoutId);
        if (idx !== -1) pendingTimeouts.splice(idx, 1);
      }, 3000);

      pendingTimeouts.push(timeoutId);
    },
  });

  return {
    sendEmail: sendEmailMutation.mutateAsync,
    isSending: sendEmailMutation.isPending,
    sendError: sendEmailMutation.error,
  };
}
